/**
 * "Is this SKU's supply working?" — answered from the SKU's OWN legs.
 *
 * ── The bug this replaces ──
 *
 * The verdict used to come from the SKU's `serverErrorRate`, and before that
 * from its channels' figures. Production 2026-09-01, `openai/gpt-image-2`:
 *
 *   ucloud-images   126 req (48.8% of the SKU)   18 failed (14.3%)   首发 100%
 *   llmport-img     120 req (46.5% of the SKU)  120 failed (100.0%)  纯兜底
 *   kkhub-images     12 req ( 4.7% of the SKU)   12 failed (100.0%)  纯兜底
 *
 * The page said 上游正常, because `llmport-img` serves OTHER SKUs fine and its
 * channel-wide rate is 0.8%. Two of three legs were failing every request this
 * SKU sent them, and 46.5% of its traffic was landing on targets configured to
 * take none of it. A verdict that contradicts a rung on the same row is worse
 * than no verdict: it tells an operator the thing they are looking at is fine.
 *
 * ── WORST leg, not a blend ──
 *
 * The verdict is the worst leg, deliberately. Averaging over legs — or reading
 * the SKU's own aggregate rate — lets a healthy high-volume leg mask a dead
 * one, which is exactly the masking that produced the bug. gpt-image-2's
 * blended failure rate is 58%, but the actionable fact is that ONE named leg
 * is at 100% and it is not even supposed to be carrying traffic.
 *
 * Legs with no traffic are skipped rather than graded: `supplySeverity` would
 * call them `normal` on a zero denominator, which would let an idle leg vote
 * "healthy". They are still a finding, but a different one — the ladder row
 * renders 无流量, and it is not this function's subject.
 *
 * ── Which rate a leg is graded on ──
 *
 * The NET one: `MeasuredLeg.failureRate`, refusals removed from both sides.
 *
 * An earlier version graded raw `errorRate` on the argument that a leg row
 * only exists once a channel was dialled, so refusals could not be in it. The
 * argument was wrong — `usage_logs` records the channel from selection, which
 * happens before the admission gate — and it reproduced the original bug one
 * level down: `claude-code/claude-fable-5`'s busiest leg rendered 78.7% and
 * marked the SKU 上游故障 when its real failure count was ONE.
 *
 * The failing case must survive the fix, and it does: `openai/gpt-image-2`'s
 * `llmport-img` leg has zero refusals, so net equals raw at 100% and it stays
 * critical. A change that made everything calm would have been a regression
 * wearing a bug fix's clothes; the two SKUs disagreeing is the check.
 *
 * A leg's net rate — not `serverErrorRate` — is the numerator, because
 * `llmport-img` returned a 4xx for every image request this SKU sent it after
 * dialling. That is a leg which cannot serve this SKU, whichever status code
 * it chose to say so with. `supplySeverity`'s thresholds still draw the line,
 * so this page and the fleet's unhealthy-upstream rule stay in step on where
 * the boundary sits; only the numerator differs, and that is stated here.
 *
 * ── And the dials the tenant never saw fail (#2240) ──
 *
 * The net rate is over tenant-FINAL rows, so a leg whose every first attempt
 * was 429'd and rescued by the fallback has a clean net rate: nothing ended
 * on it in failure. It still failed every dial. `MeasuredLeg.supplyFailureRate`
 * — 5xx + rescued attempt failures + upstream 401/402/403/429, over dials —
 * is graded alongside, and the worse of the two arms is the verdict. Both
 * arms answer "does this rung work for this SKU"; they just look at
 * different evidence, and either alone was measured to miss a broken leg.
 */

import {
  SUPPLY_FAILURE_RATE_CRITICAL,
  SUPPLY_FAILURE_RATE_WARNING,
} from '../../components/trafficHealth.ts';
import type { TrafficSeverity } from '../../components/trafficHealth.ts';
import type { MeasuredLeg, SupplyLink } from './types.ts';

/**
 * `no_traffic` and `unknown` are separate on purpose — the same distinction
 * `SupplyLink.measured` carries. One says the ladder carried nothing, the
 * other says this gateway cannot tell us. Neither is `ok`.
 */
export type SupplyVerdict = 'ok' | 'warn' | 'bad' | 'no_traffic' | 'unknown';

/**
 * One leg's verdict.
 *
 * `unknown` when the wire did not report this leg's refusals: without them
 * there is no way to tell a leg that failed everything from a leg that
 * refused everything, and those are opposite findings.
 */
/**
 * Requests below which a leg says nothing either way.
 *
 * `supplySeverity`'s own floor is 20, and borrowing it here was wrong: it
 * silenced `kkhub-images` at 12 requests and 12 failures — a leg this page's
 * own comments cite as part of the gpt-image-2 finding — so a SKU whose only
 * broken leg is small would have read 上游正常 with a 100% rung visible
 * underneath it. A verdict that contradicts a rung on the same row is the
 * failure this module exists to prevent.
 *
 * The floors differ because the questions differ. The fleet's 20 guards a
 * BROAD scan ("is this upstream unhealthy overall") against low-volume noise.
 * A leg is a TARGETED question — "does this rung work for this SKU" — asked
 * only of rungs an operator is already looking at, and 12 consecutive
 * failures answer it. Below 5 attributable requests the answer is `unknown`
 * rather than `ok`: too little evidence must never vote healthy.
 */
const LEG_MIN_REQUESTS = 5;

/** Thresholds are the fleet's; only the sample floor above differs. */
function rateSeverity(rate: number): TrafficSeverity {
  if (rate >= SUPPLY_FAILURE_RATE_CRITICAL) return 'critical';
  if (rate >= SUPPLY_FAILURE_RATE_WARNING) return 'warning';
  return 'normal';
}

/**
 * One leg's verdict, from a rate that is only ever a LOWER BOUND.
 *
 * `supply.skuChannels` carries the refusal SCALAR but no `refusedByCode` map
 * — the gateway publishes that on `summary` and `products.skus` only. So at
 * leg grain the console cannot tell a `REQUEST_NOT_SUPPORTED` (not ours) from
 * a `MODEL_NOT_AVAILABLE` (ours), and `failureRate` deducts the whole scalar,
 * which OVER-excludes. The true rate is somewhere between:
 *
 *   lower = failureRate   every refusal was legitimately not ours
 *   upper = errorRate     every refusal was ours
 *
 * Both bounds are real, so the verdict is taken from both:
 *
 *   - the lower bound already crossing a line is decisive — it can only get
 *     worse — so `warn`/`bad` stand;
 *   - a lower bound that is clean while the upper bound is not is `unknown`,
 *     NOT `ok`. That is the fail-closed half: the alternative reports a leg
 *     as healthy on the most flattering reading of data we cannot classify.
 *
 * `claude-code/claude-fable-5`'s busiest leg is exactly this case — 1.4% to
 * 78.7% — and `unknown` is the honest answer at that width. `llmport-img` has
 * zero refusals, so both bounds are 100% and it stays `bad`.
 */
export function legVerdict(leg: MeasuredLeg): SupplyVerdict {
  if (leg.dialled === 0) return 'no_traffic';
  // The #2240 arm first: it needs no refusal split, so it is decisive on its
  // own whenever the gateway reports the counters and enough was dialled.
  const supply = supplyArmVerdict(leg);
  if (supply === 'bad') return 'bad';
  const net = netArmVerdict(leg);
  return RANK[net] > RANK[supply] ? net : supply;
}

/** The dialled-volume arm: rescued attempt failures and upstream refusals. */
function supplyArmVerdict(leg: MeasuredLeg): SupplyVerdict {
  if (leg.supplyFailureRate === null) return 'ok';
  // The floor is on dials that reached an upstream — refusals never dialled.
  const dials = (leg.attributableRequests ?? leg.requests) + (leg.upstreamAttemptFailures ?? 0);
  if (dials < LEG_MIN_REQUESTS) return 'unknown';
  const severity = rateSeverity(leg.supplyFailureRate);
  return severity === 'normal' ? 'ok' : severity === 'critical' ? 'bad' : 'warn';
}

/** The tenant-final arm: the net rate as a lower bound, see above. */
function netArmVerdict(leg: MeasuredLeg): SupplyVerdict {
  if (leg.requests === 0) return 'ok';
  // The refusal counter itself is missing: not even a bound exists.
  if (leg.failureRate === null || leg.attributableRequests === null) return 'unknown';
  if (leg.attributableRequests < LEG_MIN_REQUESTS) return 'unknown';
  const lower = rateSeverity(leg.failureRate);
  if (lower !== 'normal') return lower === 'critical' ? 'bad' : 'warn';
  // Clean on the lower bound. Only trustworthy if the upper bound agrees,
  // which it does whenever there were no refusals to misclassify.
  const refusals = leg.refusedBeforeUpstream ?? 0;
  if (refusals > 0 && rateSeverity(leg.errorRate) !== 'normal') return 'unknown';
  return 'ok';
}

/**
 * The count AND rate an operator should read for a leg, from ONE arm: the
 * worse of the two. Printing the supply rate (extra failed dials + failed
 * final dials, over dials) beside the net COUNT (final failures only) read
 * `失败 49（22.4%）` for a rung whose 22.4% was 1,373 failed dials — a count
 * and a rate about different populations on one line.
 */
export function legDisplayFigures(leg: MeasuredLeg): { readonly count: number; readonly rate: number } | null {
  const supply =
    leg.supplyFailureRate === null || leg.supplyFailures === null
      ? null
      : { count: leg.supplyFailures, rate: leg.supplyFailureRate };
  const net =
    leg.failureRate === null || leg.attributableErrors === null
      ? null
      : { count: leg.attributableErrors, rate: leg.failureRate };
  if (supply === null) return net;
  if (net === null) return supply;
  return supply.rate >= net.rate ? supply : net;
}

/** The rate an operator should read for a leg: the worse of its two arms. */
export function legDisplayRate(leg: MeasuredLeg): number | null {
  return legDisplayFigures(leg)?.rate ?? null;
}

/** True when this leg's rate can only be read as "at least this much". */
export function legRateIsLowerBound(leg: MeasuredLeg): boolean {
  return (leg.refusedBeforeUpstream ?? 0) > 0;
}

/**
 * Worst-first, mirroring `HEALTH_GRADE_SEVERITY`: a leg we KNOW is broken
 * outranks one we cannot read, and either outranks silence or health. So an
 * unreported leg can never be the reason a SKU reads healthy, and can never
 * hide a leg that is measurably on fire.
 */
const RANK: Readonly<Record<SupplyVerdict, number>> = {
  bad: 4,
  warn: 3,
  unknown: 2,
  no_traffic: 1,
  ok: 0,
};

/**
 * The SKU's supply verdict, from the rungs of its own ladder.
 *
 * `reported` is `PerspectiveModel.skuChannelsReported`: false means the wire
 * carried no per-SKU leg group at all, and the answer is `unknown` rather than
 * a verdict computed from an empty set — which would come out `no_traffic` and
 * read as "this SKU dialled nobody", a claim about the fleet rather than about
 * our own visibility.
 */
export function skuSupplyVerdict(
  links: readonly SupplyLink[],
  reported: boolean,
): SupplyVerdict {
  if (!reported) return 'unknown';
  const active = links
    .map((link) => link.measured)
    .filter((leg): leg is MeasuredLeg => leg !== null && leg.dialled > 0);
  if (active.length === 0) return 'no_traffic';
  return active.reduce<SupplyVerdict>((acc, leg) => {
    const next = legVerdict(leg);
    return RANK[next] > RANK[acc] ? next : acc;
  }, 'ok');
}

/** The leg an operator should look at first, or null when none carried traffic. */
export function worstLeg(links: readonly SupplyLink[]): SupplyLink | null {
  return links.reduce<SupplyLink | null>((worst, link) => {
    const leg = link.measured;
    if (leg === null || leg.dialled === 0) return worst;
    const current = worst?.measured;
    if (current === null || current === undefined) return link;
    if (RANK[legVerdict(leg)] !== RANK[legVerdict(current)]) {
      return RANK[legVerdict(leg)] > RANK[legVerdict(current)] ? link : worst;
    }
    // Same verdict: the higher graded rate (net or supply, whichever is
    // worse) is the one to look at first. A raw comparison here would rank a
    // refusal-heavy leg above a failing one.
    return (legDisplayRate(leg) ?? 0) > (legDisplayRate(current) ?? 0) ? link : worst;
  }, null);
}

/**
 * Text tone for a verdict, shared so the ladder table and the inspector
 * cannot start disagreeing about what "warn" looks like. `unknown` warns
 * rather than reading calm: a leg we cannot grade is not a leg that is fine.
 */
export const VERDICT_TEXT_CLASS: Readonly<Record<SupplyVerdict, string>> = {
  ok: 'text-paper-low',
  warn: 'text-warn',
  bad: 'text-bad',
  no_traffic: 'text-paper-low',
  unknown: 'text-warn',
};
